// Office life, played like a little game. While the boss is "watching" (moving the mouse, typing, tapping), free
// employees stay at their desks looking busy. When the boss has been still for a few seconds they sneak off: tea,
// gossip in a huddle on the rug, a joke at a colleague's desk, a look out of the window, the bookshelf, a dance,
// cricket scores on the phone, a nap… several at once. The moment the boss moves again, whoever is off their desk
// panics and runs back pretending to work, and the "caught slacking" counter goes up.
//
// Never involves someone who is working, needs the boss, or is talking with the boss; clicking someone ends what
// they are doing. Pair chats also use the scenes from GET /ai-office/office-life (built-in plus a fresh batch
// written once a day). Everything here runs in the browser: no AI calls.
import { useCallback, useEffect, useRef, useState } from 'react';
import { getOfficeLife } from '../api';

const IDLE_MS = 9000;             // the boss "looks away" after this long without mouse, keys or touch
const GAP_AWAY = [2500, 6500];    // a new bit of mischief this often while the boss looks away
const GAP_WATCHED = [18000, 35000]; // while watched: only a stretch or a sip at the desk now and then
const MAX_AT_ONCE = 3;
const RELOAD_MS = 30 * 60 * 1000;
const rand = ([a, b]) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
const lineMs = (text) => Math.min(4200, 1600 + text.length * 50);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const today = () => new Date().toISOString().slice(0, 10);

const PANIC = ['Boss!! 😳', 'Uh oh…', 'Run!! 🏃', 'Oops 😬', 'বস আসছে!', 'Act normal…', 'Not me! 🙈'];
const BUSY = ['Working hard! 😅', 'Just checking the PI…', 'Very busy, boss 📊', 'কাজ করছি স্যার!', 'Typing… typing…', 'Almost done! 😇'];
const SOLO = {
  window: { spot: ['window0', 'window1'], lines: ['Mirpur traffic looks crazy today 🚗', 'বৃষ্টি আসবে মনে হয় ☁️', 'Is that a kite? 🪁', 'Nice weather for cha…', 'I can see my house from here 😄'] },
  shelf: { spot: ['shelf'], lines: ['Where is the 2019 file… 📚', 'This binder is older than me', 'Found it! …no, wrong one', 'Who keeps moving these? 🤨'] },
  plant: { spot: ['plant'], lines: ['Hello little plant 🌱', 'You need water, my friend 💧', 'Growing faster than our sales 😄'] },
  dance: { spot: ['sign'], lines: ['♪ ♫ 💃', 'New dance move! 🕺', '♪ Dhaka city ♫'], bounce: true },
  tea: { coffee: true, lines: ['Cha break ☕', 'Ahh… cha 😌', 'One more cup won\'t hurt ☕'] },
};
const DESK_ONLY = {
  phone: ['📱 Bangladesh 245/3! 🏏', '📱 Just one more reel…', '📱 Who posted this? 😂', '📱 Sale at Bashundhara!'],
  nap: ['💤', 'zzz…', '💤 …five more minutes'],
  stretch: ['🙆 Stretch!', 'Ahh, my back…', '*yawns* 🥱'],
  snack: ['🥟 Singara time', 'Who brought jhalmuri? 😋', '🍌 Healthy snack'],
};
const JOKES = [
  ['Why did the carton go to school? To get corrugated! 📦', '😂😂'],
  ['I asked Tally for a holiday. It said: voucher not found 😂', 'হা হা হা'],
  ['Boss said "urgent" again 😅', 'Every day is urgent!'],
  ['If PI means "Pretty Important", why is it always last minute?', 'Stop it 😂'],
  ['My salary and the AC: both arrive late 🥵', 'Too true 😂'],
  ['চা না খেলে মাথা কাজ করে না ☕', 'একদম ঠিক!'],
  ['Production said "tomorrow". Which tomorrow? 🤔', 'The famous tomorrow 😂'],
];
const GOSSIP = [
  ['Psst… did you hear? 🤫', 'What what?', 'A 50,000 carton order!', 'Boss is smiling all day 😄'],
  ['Who ate my singara? 🥟', 'Not me 👀', '…it was delicious though', 'হা হা 😂'],
  ['Cricket tonight! 🏏', 'Bangladesh will win!', 'You said that last time 😂', 'This time for sure!'],
  ['New AC coming next week?', 'Finally!! 🥳', 'Don\'t believe it till you feel it', '😂'],
  ['Did you see Alamgir bhai\'s new shirt? 👔', 'Very shiny ✨', 'Wedding season!', '🤭'],
];
// Phone calls in a corner: we only hear their side. caught = what they whisper when the boss looks back; next = the
// call they make afterwards to someone else about nearly being caught.
const CALLS = [
  { who: 'jaan', lines: ['📱 হ্যালো জান… 😍', '📱 Lunch korecho?', '📱 Na na, ami busy na, bolo 🥰'], caught: '📱 Jaan, boss is here! I have to go! 📵', next: ['📱 Hello Rina? 😅', '📱 Boss almost caught me!', '📱 …who is Jaan? Nobody! 🙈'] },
  { who: 'ammu', lines: ['📱 Ji ammu…', '📱 Haan, kheyechi 🍛', '📱 Na ammu, biye-r kotha pore… 😩'], caught: '📱 Ammu, pore call dibo! 📵', next: ['📱 Dosto, ammu abar biye-r kotha… 😩', '📱 And then boss came 😂'] },
  { who: 'best friend', lines: ['📱 Dosto! Tonight match? 🏏', '📱 Ticket pacchi na yaar', '📱 Black-e kinbo? 😏'], caught: '📱 Dosto, boss! Rakhi! 📵', next: ['📱 Hello Shila? 😇', '📱 Na na, just working… 😅'] },
  { who: 'jaan #2', lines: ['📱 Hi baby 💕', '📱 Tomake chara kauke call kori na 😇', '📱 Sotti bolchi! 🤞'], caught: '📱 Baby, network problem… 📵', next: ['📱 Hello jaan #1? 😬', '📱 Na, kauke call kori nai 🙈'] },
];
const REELS = ['📱 Cat vs cucumber 😂', '📱 Dhaka traffic dance 🕺', '📱 "Bhai, ekta selfie?" 🤣', '📱 Baby goat jumping 🐐', '📱 Cricket fail compilation 🏏😂', '📱 How to look busy at work 👀'];
// What's going on in Dhaka (the daily AI batch writes today's real ones from news and trending searches).
const DHAKA = ['Metro-te aj ki bhir! 🚇', 'Flyover-e abar jam 😩', 'Pyaj-er dam abar barlo 🧅', 'Ei viral reel dekhecho? 🔥', 'Gulshan-e notun cafe khulse ☕', 'Hatirjheel-e notun food court 🍔', 'Bashundhara-y sale cholche 🛍️'];
const REACT = ['Sotti?! 😮', 'Ami-o dekhechi! 😂', 'Weekend-e jabo 😋', 'Eta to viral 🔥', 'হা হা 😂', 'Uff, Dhaka! 😩'];
// The day's special events. The daily AI batch picks 2-4 that fit the date, weather and news (and avoids the last two
// weeks); without it the browser picks them itself. Their lines are said in order by different people.
const EVENT_LINES = {
  powercut: ['আবার কারেন্ট গেল! 😩', 'Torch on! 🔦', 'Generator kothay?!', 'IPS-o dead 😭', 'AC chara ami shesh 🥵', 'কারেন্ট আসছে! 🎉'],
  chawala: ['Cha! Garam cha! ☕', 'Mama, ek cup dudh cha!', 'Amar-ta chini kom', 'Ar ekta singara dao 🥟', 'Mama, baki thakbe 😅', 'Kalke dibo, promise!'],
  birthday: ['🎂 Happy birthday to you ♪', '♪ Happy birthday dear {b} ♪', 'Speech! Speech! 🎤', 'Thank you sobai 🥹', 'Cake-er boro piece amar 🍰', 'Treat kobe? 😏'],
  cricket: ['Score koto?', 'SIXXX!!! 🏏', 'OUT?! No way 😱', 'Third umpire dekho!', 'Ei over-e jitbo!', 'Bangladesh! Bangladesh! 🇧🇩'],
  cat: ['Ei biral! 🐱', 'My keyboard! 😾', 'Ke anlo biral-ta?', 'Dhoro dhoro! 🏃', 'Ok it can stay. Office cat 😻'],
  rain: ['Bristi! 🌧️', 'Aj khichuri must 🍲', 'Ilish bhaja-o lagbe 🐟', 'Rickshaw paoa jabe na 😩', 'Mirpur-e pani jomse 😱'],
  weekend: ['Kal chhuti! 🥳', 'Weekend plan: ghum 😴', '♪ Thursday evening ♪', 'Cox\'s Bazar ke jacche? 🏖️', 'Ami Netflix-er shathe 😎', 'Kaj? Sunday dekhbo 😂'],
};
// How often each event may happen in one day.
const EVENT_QUOTA = { powercut: 1, chawala: 2, birthday: 1, cricket: 2, cat: 2, rain: 2, weekend: 2 };
const EVENT_GAP = [70000, 200000]; // between events while the boss looks away
const CHAWALA = { shirt: '#E8E2D0', hair: '#1A1A1A', skin: '#8A5A3C', long: false };
// Small seeded random for the browser's own choice of the day's events (same choices all day, different each day).
const seeded = (seed) => { let x = 0; for (const ch of seed) x = (x * 31 + ch.charCodeAt(0)) % 2147483647; return () => { x = (x * 48271) % 2147483647; return x / 2147483647; }; };
const bdDay = () => { const d = new Date(Date.now() + (6 * 60 + new Date().getTimezoneOffset()) * 60000); return { dow: d.getDay(), hour: d.getHours(), month: d.getMonth() + 1 }; };
const weekendMood = () => { const { dow, hour } = bdDay(); return (dow === 4 && hour >= 15) || dow === 5 || dow === 6; };
const readDay = (key) => { try { const v = JSON.parse(localStorage.getItem(key) || '{}'); return v.day === today() ? v : { day: today() }; } catch (_) { return { day: today() }; } };
const writeDay = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch (_) { /* private window */ } };

const PLANE = [['✈️ Incoming!', 'Hey!! 😠'], ['✈️ Catch!', 'Missed me 😝'], ['✈️ Air mail!', '…very mature 🙄']];

function bestActor(v) {
  const top = Object.entries(v.n || {}).sort((a, b) => b[1] - a[1])[0];
  return top && top[1] >= 2 ? { id: top[0], n: top[1] } : null;
}

// Today's events: the AI batch's choice, or the browser's own (seeded by the date, never yesterday's mix again).
function todaysEvents(fun, agents) {
  if (fun && fun.events && fun.events.length) return fun.events;
  const day = today();
  const rnd = seeded(day);
  const { month } = bdDay();
  const prev = (() => { try { return JSON.parse(localStorage.getItem('aio-events-prev') || '{}'); } catch (_) { return {}; } })();
  const yesterday = prev.day !== day ? prev.kinds || [] : prev.before || [];
  const weights = { powercut: 3, chawala: 3, birthday: 1.5, cricket: 2, cat: 2.5, rain: month >= 6 && month <= 10 ? 3 : 0.6 };
  const kinds = [];
  const n = 2 + Math.floor(rnd() * 2);
  for (let i = 0; i < 10 && kinds.length < n; i++) {
    const pool = Object.keys(weights).filter((k) => !kinds.includes(k));
    let total = pool.reduce((t, k) => t + weights[k] * (yesterday.includes(k) ? 0.2 : 1), 0), r = rnd() * total, kind = pool[0];
    for (const k of pool) { r -= weights[k] * (yesterday.includes(k) ? 0.2 : 1); if (r <= 0) { kind = k; break; } }
    kinds.push(kind);
  }
  if (weekendMood()) kinds.push('weekend');
  try { if (prev.day !== day) localStorage.setItem('aio-events-prev', JSON.stringify({ day, kinds, before: prev.kinds || [] })); } catch (_) { /* private window */ }
  const ids = (agents || []).map((a) => a.id);
  return kinds.map((kind) => ({ kind, lines: shuffle(EVENT_LINES[kind]).slice(0, kind === 'birthday' ? 6 : 5), who: kind === 'birthday' && ids.length ? ids[Math.floor(rnd() * ids.length)] : null }));
}

export default function useOfficeLife({ canvasRef, agents, chatId, active }) {
  const scenes = useRef([]);
  const fun = useRef(null); // today's lines from the daily AI batch (theme, calls, jokes…)
  const lastCaller = useRef(null); // { id, call } caught on the phone: they call someone else about it later
  const [theme, setTheme] = useState(null);
  const live = useRef({ agents, chatId, active });
  live.current = { agents, chatId, active };
  const running = useRef(new Set()); // { cast: [ids], aborted }
  const lastInput = useRef(Date.now());
  const lastKinds = useRef([]);
  const openedAt = useRef(Date.now());
  const [actor, setActor] = useState(() => { const v = readDay('aio-actors'); return bestActor(v); });
  const [caught, setCaught] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem('aio-caught') || '{}'); return c.day === today() ? c.n || 0 : 0; } catch (_) { return 0; }
  });

  useEffect(() => {
    let alive = true;
    const load = () => getOfficeLife().then((r) => {
      if (!alive) return;
      scenes.current = r.scenes || [];
      fun.current = r.fun || null;
      setTheme((r.fun && r.fun.theme) || null);
    }, () => {});
    load();
    const t = setInterval(load, RELOAD_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Built-in lines mixed with today's fresh ones (fresh ones a bit more often, so each day feels different).
  const lines = (builtIn, key) => {
    const f = fun.current && fun.current[key];
    return f && f.length && Math.random() < 0.65 ? f : builtIn;
  };
  const inScene = (id) => [...running.current].some((s) => s.cast.includes(id));
  const free = (id) => {
    const { agents: list, chatId: chat } = live.current;
    const a = list.find((x) => x.id === id);
    return a && a.id !== chat && (a.status === 'idle' || !a.status) && a.desk != null && a.desk >= 0;
  };
  // Undo a scene's effects (lights back on, visitors leave, cake away). Runs once.
  const endScene = (sc) => { const fns = sc.cleanup || []; sc.cleanup = []; fns.forEach((f) => { try { f(); } catch (_) { /* canvas gone */ } }); };
  const nameOf = (id) => (live.current.agents.find((x) => x.id === id) || {}).name || '';

  // Ends scenes now (all, or the one with this person): bubbles off, everyone walks back to their desk.
  const interrupt = useCallback((onlyId) => {
    const cv = canvasRef.current;
    for (const s of [...running.current]) {
      if (onlyId && !s.cast.includes(onlyId)) continue;
      s.aborted = true;
      running.current.delete(s);
      endScene(s);
      if (!cv) continue;
      for (const id of s.cast) {
        cv.say(id, null);
        cv.setLocal(id, { bounce: false });
        if (!cv.isSeated(id)) cv.walkTo(id, 'desk').then(() => cv.setLocal(id, { mug: false }));
      }
    }
  }, [canvasRef]);

  // The boss looked back: whoever is off their desk panics and runs back pretending to work.
  const caughtThem = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const away = [];
    for (const s of [...running.current]) {
      s.aborted = true;
      running.current.delete(s);
      endScene(s);
      for (const id of s.cast) {
        cv.setLocal(id, { bounce: false });
        if (!cv.isSeated(id)) away.push({ id, line: s.kind === 'call' ? s.call.caught : s.kind === 'call2' ? pick(['📱 Rakhi, rakhi! 📵', '📱 Again?! Bye! 📵', '📱 Call you later! 🙈']) : null });
        else cv.say(id, null);
        if (s.call && s.kind === 'call') lastCaller.current = { id, call: s.call };
      }
    }
    if (!away.length) return;
    away.forEach(({ id, line }, i) => {
      cv.say(id, line || pick(lines(PANIC, 'panic')));
      wait(500 + i * 120).then(() => cv.walkTo(id, 'desk', { speed: 2.6 })).then(() => {
        if (inScene(id)) return;
        cv.setLocal(id, { mug: false, fakeWork: true });
        cv.say(id, pick(lines(BUSY, 'busy')));
        const v = readDay('aio-actors');
        v.n = { ...(v.n || {}), [id]: ((v.n || {})[id] || 0) + 1 };
        writeDay('aio-actors', v);
        setActor(bestActor(v));
        return wait(2600).then(() => { if (!inScene(id)) { cv.say(id, null); cv.setLocal(id, { fakeWork: false }); } });
      });
    });
    setCaught((n) => {
      const next = n + away.length;
      try { localStorage.setItem('aio-caught', JSON.stringify({ day: today(), n: next })); } catch (_) { /* private window */ }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef]);

  // Boss presence: any mouse movement, key, scroll or touch on the page. Throttled.
  useEffect(() => {
    let lastSeen = 0;
    const seen = () => {
      const now = Date.now();
      if (now - lastSeen < 250) return;
      lastSeen = now;
      const wasAway = now - lastInput.current >= IDLE_MS;
      lastInput.current = now;
      if (wasAway && live.current.active) caughtThem();
    };
    const vis = () => { if (!document.hidden) seen(); };
    const evs = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart'];
    evs.forEach((e) => window.addEventListener(e, seen, { passive: true }));
    document.addEventListener('visibilitychange', vis);
    return () => { evs.forEach((e) => window.removeEventListener(e, seen)); document.removeEventListener('visibilitychange', vis); };
  }, [caughtThem]);

  useEffect(() => {
    let timer = null;
    let stopped = false;

    // Runs one activity for its cast; every step checks it is still on.
    async function play(kind, cast, extra) {
      const cv = canvasRef.current;
      const s = { cast, aborted: false, kind, call: kind === 'call' || kind === 'call2' ? extra : null, cleanup: [] };
      running.current.add(s);
      const ok = () => !s.aborted && !stopped && !!canvasRef.current && cast.every(free);
      const say = async (id, text, ms) => { if (!ok()) return false; cv.say(id, text); await wait(ms || lineMs(text)); return ok(); };
      const home = async (ids) => {
        ids.forEach((id) => cv.say(id, null));
        await Promise.all(ids.map((id, i) => wait(i * 400).then(() => (ok() ? cv.walkTo(id, 'desk') : null))));
        ids.forEach((id) => cv.setLocal(id, { mug: false, bounce: false }));
      };
      try {
        const [a, b, c] = cast;
        if (kind === 'event') {
          await playEvent(cv, s, extra, cast, ok, say, home);
        } else if (kind === 'dhaka') {
          const host = live.current.agents.find((x) => x.id === b);
          await cv.walkTo(a, { beside: host.desk });
          if (!ok()) return;
          if (!(await say(a, pick(lines(DHAKA, 'dhaka'))))) return;
          cv.say(a, null);
          if (!(await say(b, pick(REACT), 2200))) return;
          cv.say(b, null);
          await home([a]);
        } else if (kind === 'call' || kind === 'call2') {
          // Sneaks off to a corner with the phone; we only hear their side.
          await cv.walkTo(a, { spot: pick(['hall', 'plant', 'window1']) });
          if (!ok()) return;
          const said = kind === 'call' ? extra.lines : extra.next;
          for (const line of said) {
            cv.setLocal(a, { bounce: /😍|🥰|💕|😂/.test(line) });
            if (!(await say(a, line.startsWith('📱') ? line : `📱 ${line}`))) return;
          }
          cv.setLocal(a, { bounce: false });
          await home([a]);
        } else if (kind === 'reel') {
          if (!(await say(a, pick(lines(REELS, 'reels')), 2600))) return;
          cv.setLocal(a, { bounce: true });
          await say(a, pick(['😂😂😂', 'হা হা হা 🤣', 'LOL 😂', 'Ki dekhlam! 🤣']), 1800);
          cv.setLocal(a, { bounce: false }); cv.say(a, null);
        } else if (kind in DESK_ONLY) {
          if (kind === 'stretch') cv.setLocal(a, { bounce: true });
          await say(a, pick(DESK_ONLY[kind]), kind === 'nap' ? 5000 : 3200);
          cv.setLocal(a, { bounce: false }); cv.say(a, null);
        } else if (kind in SOLO) {
          const act = SOLO[kind];
          await cv.walkTo(a, act.coffee ? { coffee: Math.floor(Math.random() * 2) } : { spot: pick(act.spot) });
          if (!ok()) return;
          if (act.coffee) cv.setLocal(a, { mug: true });
          if (act.bounce) cv.setLocal(a, { bounce: true });
          const pool = kind === 'window' ? lines(act.lines, 'window') : act.lines;
          for (const line of shuffle(pool).slice(0, 2)) if (!(await say(a, line))) return;
          await home([a]);
        } else if (kind === 'joke') {
          const host = live.current.agents.find((x) => x.id === b);
          await cv.walkTo(a, { beside: host.desk });
          if (!ok()) return;
          const [j, r] = pick(lines(JOKES, 'jokes'));
          if (!(await say(a, j))) return;
          cv.say(a, null); cv.setLocal(b, { bounce: true }); cv.setLocal(a, { bounce: true });
          if (!(await say(b, r, 2200))) return;
          cv.setLocal(b, { bounce: false }); cv.say(b, null);
          await home([a]);
        } else if (kind === 'plane') {
          const [x, y] = pick(PLANE);
          if (!(await say(a, x, 1800))) return;
          cv.say(a, null);
          await say(b, y, 2200);
          cv.say(b, null);
        } else if (kind === 'gossip') {
          const spots = shuffle(['rug0', 'rug1', 'rug2', 'rug3']);
          await Promise.all(cast.map((id, i) => wait(i * 600).then(() => cv.walkTo(id, { spot: spots[i] }))));
          if (!ok()) return;
          const talk = pick(lines(GOSSIP, 'gossip'));
          for (let i = 0; i < talk.length; i++) {
            const who = cast[i % cast.length];
            cast.forEach((id) => id !== who && cv.say(id, null));
            if (!(await say(who, talk[i]))) return;
          }
          cast.forEach((id) => cv.setLocal(id, { bounce: true }));
          cast.forEach((id) => cv.say(id, null));
          if (!(await say(c || a, '😂😂😂', 1500))) return;
          await home(cast);
        } else if (kind === 'scene') {
          // A pair chat or tea break from the server's scenes (built-in + the day's fresh ones).
          const scene = extra;
          if (scene.kind === 'coffee') {
            await Promise.all([cv.walkTo(a, { coffee: 0 }), wait(700).then(() => cv.walkTo(b, { coffee: 1 }))]);
            cv.setLocal(a, { mug: true }); cv.setLocal(b, { mug: true });
          } else {
            const host = live.current.agents.find((x) => x.id === b);
            await cv.walkTo(a, { beside: host.desk });
          }
          if (!ok()) return;
          for (const [who, text] of scene.lines) {
            const speaker = cast[who], other = cast[1 - who];
            const named = text.replace(/\{a\}/g, nameOf(a)).replace(/\{b\}/g, nameOf(b));
            cv.say(other, null);
            if (!(await say(speaker, named))) return;
          }
          await home(scene.kind === 'coffee' ? [a, b] : [a]);
        }
      } catch (_) {
        /* the canvas went away mid-scene */
      } finally {
        endScene(s);
        const wasRunning = running.current.delete(s);
        // Ended by a status change (work, a question for the boss…), not by the boss: walk back calmly.
        if (wasRunning && (s.aborted || !ok()) && cv) {
          for (const id of cast) { cv.say(id, null); cv.setLocal(id, { bounce: false }); if (!cv.isSeated(id)) cv.walkTo(id, 'desk').then(() => cv.setLocal(id, { mug: false })); }
        }
      }
    }

    // One of the day's events, played by everyone who is free. Effects are undone by the scene's cleanup, also when
    // the boss catches them in the middle.
    async function playEvent(cv, s, ev, cast, ok, say, home) {
      const talk = ev.lines.map((l) => l.replace(/\{b\}/g, nameOf(ev.who) || nameOf(cast[0])));
      const speak = async (i) => { const who = cast[i % cast.length]; cast.forEach((id) => id !== who && cv.say(id, null)); return say(who, talk[i]); };
      const all = async (from = 0, to = talk.length) => { for (let i = from; i < to; i++) if (!(await speak(i))) return false; return true; };
      if (ev.kind === 'powercut') {
        cv.setEffect({ dark: true });
        cast.forEach((id) => cv.setLocal(id, { torch: true }));
        s.cleanup.push(() => { cv.setEffect({ dark: false }); cast.forEach((id) => cv.setLocal(id, { torch: false, bounce: false })); });
        if (!(await all(0, talk.length - 1))) return;
        cv.setEffect({ dark: false });
        cast.forEach((id) => cv.setLocal(id, { torch: false, bounce: true }));
        await speak(talk.length - 1);
        cast.forEach((id) => { cv.setLocal(id, { bounce: false }); cv.say(id, null); });
      } else if (ev.kind === 'chawala') {
        cv.addVisitor('chawala', 'person', CHAWALA);
        s.cleanup.push(() => { cv.sayVisitor('chawala', 'Abar ashbo! 👋'); cv.walkVisitor('chawala', 'door').then(() => cv.removeVisitor('chawala')); });
        await cv.walkVisitor('chawala', 'meet');
        if (!ok()) return;
        cv.sayVisitor('chawala', talk[0]);
        await Promise.all(cast.map((id, i) => wait(i * 400).then(() => cv.walkTo(id, 'meet'))));
        cv.sayVisitor('chawala', null);
        if (!ok()) return;
        cast.forEach((id) => cv.setLocal(id, { mug: true }));
        if (!(await all(1))) return;
        cv.sayVisitor('chawala', 'Dhonnobad! Abar ashbo 👋');
        await wait(1500);
        endScene(s);
        await home(cast);
      } else if (ev.kind === 'birthday') {
        cv.setEffect({ cake: true });
        s.cleanup.push(() => cv.setEffect({ cake: false }));
        await Promise.all(cast.map((id, i) => wait(i * 400).then(() => cv.walkTo(id, 'meet'))));
        if (!ok()) return;
        cast.forEach((id) => cv.setLocal(id, { bounce: true }));
        if (!(await all())) return;
        cast.forEach((id) => cv.setLocal(id, { bounce: false }));
        await home(cast);
      } else if (ev.kind === 'cricket' || ev.kind === 'weekend') {
        await Promise.all(cast.map((id, i) => wait(i * 400).then(() => cv.walkTo(id, ev.kind === 'cricket' ? 'meet' : { spot: pick(['sign', 'hall', 'window1', 'rug1']) }))));
        if (!ok()) return;
        for (let i = 0; i < talk.length; i++) {
          cast.forEach((id) => cv.setLocal(id, { bounce: /!|🎉|🥳|♪|🏏/.test(talk[i]) }));
          if (!(await speak(i))) return;
        }
        cast.forEach((id) => cv.setLocal(id, { bounce: false }));
        await home(cast);
      } else if (ev.kind === 'cat') {
        const victim = pick(live.current.agents.filter((x) => x.desk != null && x.desk >= 0));
        cv.addVisitor('cat', 'cat');
        s.cleanup.push(() => { cv.sayVisitor('cat', 'Meow! 🐾'); cv.walkVisitor('cat', 'door').then(() => cv.removeVisitor('cat')); });
        await cv.walkVisitor('cat', { desk: victim.desk });
        if (!ok()) return;
        cv.sayVisitor('cat', 'Meow 🐾');
        const chasers = cast.slice(0, 2);
        if (!(await speak(0))) return;
        await Promise.all(chasers.map((id, i) => wait(i * 300).then(() => cv.walkTo(id, { beside: victim.desk }))));
        cv.sayVisitor('cat', null);
        if (!ok()) return;
        if (!(await all(1))) return;
        endScene(s);
        await home(chasers);
      } else if (ev.kind === 'rain') {
        const watchers = cast.slice(0, 3);
        await Promise.all(watchers.map((id, i) => wait(i * 400).then(() => cv.walkTo(id, { spot: ['window0', 'window1', 'sign'][i] }))));
        if (!ok()) return;
        for (let i = 0; i < talk.length; i++) { const who = watchers[i % watchers.length]; watchers.forEach((id) => id !== who && cv.say(id, null)); if (!(await say(who, talk[i]))) return; }
        await home(watchers);
      }
    }

    function chooseEvent() {
      const plays = readDay('aio-event-plays');
      const evs = todaysEvents(fun.current, live.current.agents).filter((e) => ((plays.n || {})[e.kind] || 0) < (EVENT_QUOTA[e.kind] || 1));
      if (!evs.length) return null;
      const freeIds = shuffle(live.current.agents.filter((a) => free(a.id) && !inScene(a.id)).map((a) => a.id));
      if (freeIds.length < 2) return null;
      const ev = pick(evs);
      plays.n = { ...(plays.n || {}), [ev.kind]: ((plays.n || {})[ev.kind] || 0) + 1 };
      writeDay('aio-event-plays', plays);
      // The birthday person (if free) is in the middle of it.
      const cast = ev.kind === 'birthday' && ev.who && freeIds.includes(ev.who) ? [ev.who, ...freeIds.filter((x) => x !== ev.who)] : freeIds;
      return { kind: 'event', cast, extra: ev };
    }

    function choose(watched) {
      const freeIds = shuffle(live.current.agents.filter((a) => free(a.id) && !inScene(a.id)).map((a) => a.id));
      if (!freeIds.length) return null;
      if (watched) return { kind: pick(['stretch', 'snack', 'stretch']), cast: [freeIds[0]] };
      const options = [];
      const add = (kind, n, w, extra) => { if (freeIds.length >= n && !lastKinds.current.slice(-2).includes(kind)) for (let i = 0; i < w; i++) options.push({ kind, n, extra }); };
      add('gossip', 3, 3);
      add('joke', 2, 3);
      add('plane', 2, 1);
      add('scene', 2, 3);
      add('call', 1, 3);
      add('dhaka', 2, fun.current && fun.current.dhaka && fun.current.dhaka.length ? 4 : 2);
      add('reel', 1, 2);
      // Caught on the phone earlier? They call someone else about it as soon as they can.
      const lc = lastCaller.current;
      if (lc && freeIds.includes(lc.id) && lc.call.next && lc.call.next.length) {
        lastCaller.current = null;
        lastKinds.current = [...lastKinds.current, 'call2'].slice(-4);
        return { kind: 'call2', cast: [lc.id], extra: lc.call };
      }
      Object.keys(SOLO).forEach((k) => add(k, 1, 2));
      Object.keys(DESK_ONLY).forEach((k) => add(k, 1, 1));
      if (!options.length) return null;
      const choice = pick(options);
      let cast = freeIds.slice(0, choice.n);
      if (choice.kind === 'call') choice.extra = pick(lines(CALLS, 'calls'));
      if (choice.kind === 'scene') {
        const pool = scenes.current.filter((s) => !s.cast || s.cast.every((id) => freeIds.includes(id)));
        if (!pool.length) return null;
        choice.extra = pick(pool);
        if (choice.extra.cast) cast = choice.extra.cast;
      }
      lastKinds.current = [...lastKinds.current, choice.kind].slice(-4);
      return { kind: choice.kind, cast, extra: choice.extra };
    }

    // Checks every second; the next bit of mischief comes soon after the boss looks away.
    let nextAt = Date.now() + 4000;
    let nextEventAt = Date.now() + rand([25000, 60000]);
    let wasWatched = true;
    function tick() {
      timer = null;
      const now = Date.now();
      const watched = now - lastInput.current < IDLE_MS;
      if (wasWatched && !watched) nextAt = Math.min(nextAt, now + 1200);
      wasWatched = watched;
      const cv = canvasRef.current;
      const on = live.current.active && !document.hidden && cv;
      // A special event (power cut, cha-wala, cat…) now and then while the boss looks away, when the floor is quiet.
      if (on && !watched && now >= nextEventAt && running.current.size === 0) {
        const ev = chooseEvent();
        if (ev) play(ev.kind, ev.cast, ev.extra);
        nextEventAt = now + rand(EVENT_GAP);
      } else if (on && now >= nextAt && !(now >= nextEventAt && !watched) && running.current.size < (watched ? 1 : MAX_AT_ONCE)) {
        // (An event is due: let the floor clear first instead of starting something new.)
        const pickIt = choose(watched);
        if (pickIt) play(pickIt.kind, pickIt.cast, pickIt.extra);
        // Thursday evening and the weekend: even lazier.
        const gap = watched ? GAP_WATCHED : weekendMood() ? [1500, 4000] : GAP_AWAY;
        nextAt = now + rand(gap);
      }
      if (!stopped) timer = setTimeout(tick, 1000);
    }

    timer = setTimeout(tick, 1000);
    // Developer machines only: window.__aioOfficeEvent('cat') plays an event now (for testing the drawings).
    if (process.env.NODE_ENV === 'development') {
      window.__aioOfficeEvent = (kind) => {
        interrupt();
        const freeIds = live.current.agents.filter((a) => free(a.id) && !inScene(a.id)).map((a) => a.id);
        if (EVENT_LINES[kind] && freeIds.length >= 2) play('event', freeIds, { kind, lines: EVENT_LINES[kind], who: freeIds[0] });
      };
    }
    return () => { stopped = true; if (timer) clearTimeout(timer); interrupt(); };
  }, [canvasRef, interrupt]);

  // Someone in a scene got busy (work, needs the boss) or the boss opened their chat: end that scene.
  useEffect(() => {
    for (const s of [...running.current]) if (!s.cast.every(free)) interrupt(s.cast.find((id) => !free(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, chatId]);

  useEffect(() => { if (!active) interrupt(); }, [active, interrupt]);

  // A title for the boss, from today's catches.
  const badge = caught >= 25 ? '🦅 Eagle-eye boss' : caught >= 10 ? '👔 Strict boss' : caught >= 3 ? '👀 Watchful boss'
    : caught === 0 && Date.now() - openedAt.current > 20 * 60 * 1000 ? '😎 Chill boss' : null;
  const actorName = actor ? nameOf(actor.id) : null;
  return { interrupt, caught, theme, badge, actor: actorName ? { name: actorName, n: actor.n } : null };
}
